/**
 * Column mapping — what the operator sees between picking a file and the
 * import actually running.
 *
 * The old contract was "download our model, fill it, upload it": any
 * other shape was rejected or silently half-read. Now they upload THEIR
 * file and answer one question per column — is this a beneficiary field,
 * an address field, a campaign question, a custom field, or nothing we
 * care about? Nothing is parsed or written until they confirm.
 *
 * Everything is pre-filled from the server's suggestion, so a file that
 * did come from our model is a single "Import" click, and a foreign file
 * arrives mostly mapped with the leftovers set to "ignore".
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiPaths, api } from "@/lib/api";
import i18n from "@/lib/i18n";
import { describeApiError } from "@/lib/apiErrors";
import { COMMENT_KINDS } from "@/lib/types";
import type { QuestionKind } from "@/lib/types";

type TargetKind =
  | "ignore"
  | "beneficiary"
  | "address"
  | "question"
  | "custom_field";

type CatalogEntry = {
  key: string;
  label: string;
  required?: boolean;
  kind?: string;
  applies_to?: string;
  country_scope?: string;
  /** Backlog 2026-09-21 — question entries only. Flags that mapping a
   *  column here feeds the campaign report's conform/non-conform tally,
   *  not just an ordinary answer. */
  is_conformity?: boolean;
  conformity_value?: "yes" | "no" | "";
};

type ColumnInfo = { index: number; header: string; samples: string[] };

/** The four groups a column can be mapped into, as the server returns
 * them. Spelled out (not a Record) so `noUncheckedIndexedAccess` doesn't
 * make every group possibly-undefined at each use site. */
type Catalog = {
  beneficiary: CatalogEntry[];
  address: CatalogEntry[];
  question: CatalogEntry[];
  custom_field: CatalogEntry[];
};

type ColumnsResponse = {
  job_id: string;
  source_filename: string;
  sheets: string[];
  sheet: string;
  header_row: number;
  columns: ColumnInfo[];
  catalog: Catalog;
  suggested: Record<string, MappingEntry>;
  required_beneficiary_keys: string[];
  reused_from_job: string | null;
};

type NewFieldSpec = {
  label: string;
  kind: string;
  applies_to?: string;
  /** Question kinds only. Backlog 2026-09-22 — a question created here
   *  could not be a conformity check or carry a comment box, so the
   *  operator had to finish the import and then set it again in the
   *  questions editor. */
  comment_trigger?: "always" | "yes" | "no" | "nsp" | "";
  is_conformity?: boolean;
  conformity_value?: "yes" | "no" | "";
};

export type MappingEntry = {
  target: TargetKind;
  key?: string;
  applies_to?: string;
  create?: NewFieldSpec;
};

type RowError = { column: number | null; message: string };

const CUSTOM_FIELD_KINDS = [
  "text",
  "textarea",
  "number",
  "boolean",
  "date",
  "email",
];
// Backlog 2026-09-22 — `boolean_comment` (Yes/No + comment) shipped
// earlier today but this list was never updated, so the kind simply
// wasn't offered when creating a question from the mapping screen.
// The tri-states (2026-10-01) are added here for the same reason.
const QUESTION_KINDS = [
  "text",
  "textarea",
  "number",
  "boolean",
  "boolean_comment",
  "tristate",
  "tristate_comment",
  "date",
  "rating",
];
/** Kinds that can act as a campaign conformity check. Mirrors
 *  `_CONFORMITY_KINDS` on the backend serializer. */
const CONFORMITY_KINDS = new Set([
  "boolean",
  "boolean_comment",
  "tristate",
  "tristate_comment",
]);
const APPLIES_TO = ["contact", "address", "membership"];

/** One color per mapping target so the row's current destination reads
 *  at a glance instead of only living inside the <select> text — the
 *  main lever for making a 5+ column mapping screen scannable instead
 *  of a wall of identical dropdowns. */
const TARGET_META: Record<
  TargetKind,
  { dot: string; text: string; ring: string; labelKey: string; fallback: string }
> = {
  ignore: {
    dot: "bg-nb-text-3",
    text: "text-nb-text-3",
    ring: "ring-nb-border",
    labelKey: "leads.mapping.ignore",
    fallback: "Don't import",
  },
  beneficiary: {
    dot: "bg-nb-mint",
    text: "text-nb-mint",
    ring: "ring-nb-mint/30",
    labelKey: "leads.mapping.groupBeneficiary",
    fallback: "Beneficiary",
  },
  address: {
    dot: "bg-nb-purple",
    text: "text-nb-purple",
    ring: "ring-nb-purple/30",
    labelKey: "leads.mapping.groupAddress",
    fallback: "Address",
  },
  question: {
    dot: "bg-nb-orange",
    text: "text-nb-orange",
    ring: "ring-nb-orange/30",
    labelKey: "leads.mapping.groupQuestion",
    fallback: "Campaign question",
  },
  custom_field: {
    dot: "bg-nb-pink",
    text: "text-nb-pink",
    ring: "ring-nb-pink/30",
    labelKey: "leads.mapping.groupCustomField",
    fallback: "Custom field",
  },
};

function TargetBadge({ target }: { target: TargetKind }) {
  const { t } = useTranslation();
  const meta = TARGET_META[target];
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full bg-nb-surface px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ${meta.ring} ${meta.text}`}
    >
      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${meta.dot}`} />
      {t(meta.labelKey, meta.fallback)}
    </span>
  );
}

/** Encode an entry as the <select> value; `create:*` opens the inline form. */
function encode(entry: MappingEntry | undefined): string {
  if (!entry || entry.target === "ignore") return "ignore";
  if (entry.create) return `create:${entry.target}`;
  if (entry.target === "custom_field") {
    return `custom_field:${entry.applies_to ?? "contact"}:${entry.key}`;
  }
  return `${entry.target}:${entry.key}`;
}

function decode(value: string, header: string): MappingEntry {
  if (value === "ignore") return { target: "ignore" };
  if (value === "create:custom_field") {
    return {
      target: "custom_field",
      create: { label: header, kind: "text", applies_to: "contact" },
    };
  }
  if (value === "create:question") {
    return { target: "question", create: { label: header, kind: "text" } };
  }
  const parts = value.split(":");
  if (parts[0] === "custom_field") {
    return {
      target: "custom_field",
      applies_to: parts[1],
      key: parts.slice(2).join(":"),
    };
  }
  return { target: parts[0] as TargetKind, key: parts.slice(1).join(":") };
}

/** Stable identity of a mapped target — used to spot two columns fighting
 * over one field. A custom field is only unique WITH its applies_to. */
function identity(entry: MappingEntry): string | null {
  if (entry.target === "ignore" || entry.create) return null;
  if (entry.target === "custom_field") {
    return `custom_field:${entry.applies_to}:${entry.key}`;
  }
  return `${entry.target}:${entry.key}`;
}

export function ImportMappingModal({
  campaignId,
  jobId,
  onCancel,
  onConfirmed,
}: {
  campaignId: string;
  jobId: string;
  onCancel: () => void;
  onConfirmed: () => void;
}) {
  const { t } = useTranslation();
  const [sheet, setSheet] = useState<string | null>(null);
  const [headerRow, setHeaderRow] = useState<number | null>(null);
  const [mapping, setMapping] = useState<Record<string, MappingEntry>>({});
  const [serverErrors, setServerErrors] = useState<RowError[]>([]);

  const columns = useQuery({
    queryKey: ["import-columns", campaignId, jobId, sheet, headerRow],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (sheet) params.sheet = sheet;
      if (headerRow) params.header_row = String(headerRow);
      // Reported 2026-09-23 — "I've the UI in English but the field
      // types are rendered in French." The catalog used to be localised
      // from the campaign's COUNTRY, which is right for the downloaded
      // Excel template and wrong for a dropdown the operator reads.
      const res = await api.get<ColumnsResponse>(
        `${ApiPaths.campaignImportColumns(campaignId, jobId)}?lang=${i18n.language}`,
        { params },
      );
      return res.data;
    },
  });

  const data = columns.data;

  // Seed from the server's suggestion whenever the inspected shape
  // changes (first load, or the operator switched sheet / header row).
  useEffect(() => {
    if (data) setMapping({ ...data.suggested });
  }, [data]);

  const duplicates = useMemo(() => {
    const seen = new Map<string, number>();
    const dupes = new Set<number>();
    for (const [idx, entry] of Object.entries(mapping)) {
      const id = identity(entry);
      if (!id) continue;
      const prior = seen.get(id);
      if (prior !== undefined) {
        dupes.add(prior);
        dupes.add(Number(idx));
      } else {
        seen.set(id, Number(idx));
      }
    }
    return dupes;
  }, [mapping]);

  const missingRequired = useMemo(() => {
    if (!data) return [];
    const mapped = new Set(
      Object.values(mapping)
        .filter((e) => e.target === "beneficiary" && !e.create)
        .map((e) => e.key),
    );
    // A legal-entity beneficiary has no first_name/last_name — its
    // legal_name IS its identity (2026-09-08, mirrors the backend's
    // validate_mapping — see import_mapping.py). Without this check
    // the Confirm button would stay disabled forever on a legal_name-
    // only mapping even though the server now accepts it.
    if (mapped.has("legal_name")) return [];
    return data.required_beneficiary_keys
      .filter((key) => !mapped.has(key))
      .map(
        (key) =>
          data.catalog.beneficiary.find((e) => e.key === key)?.label ?? key,
      );
  }, [data, mapping]);

  const confirm = useMutation({
    mutationFn: async () => {
      const res = await api.post(
        ApiPaths.campaignImportMapping(campaignId, jobId),
        {
          sheet: data?.sheet ?? "",
          header_row: data?.header_row ?? 1,
          columns: mapping,
          headers: Object.fromEntries(
            (data?.columns ?? []).map((c) => [String(c.index), c.header]),
          ),
        },
      );
      return res.data;
    },
    onSuccess: () => {
      setServerErrors([]);
      onConfirmed();
    },
    onError: (err: unknown) => {
      const payload = (
        err as { response?: { data?: { errors?: RowError[] } } }
      )?.response?.data;
      setServerErrors(payload?.errors ?? []);
    },
  });

  const blocked = missingRequired.length > 0 || duplicates.size > 0;

  function setEntry(index: number, entry: MappingEntry) {
    setMapping((prev) => ({ ...prev, [String(index)]: entry }));
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("leads.mapping.title", "Match your columns")}
      className="modal-backdrop bg-black/70"
    >
      <div className="modal-panel flex w-full max-w-5xl flex-col gap-4 max-h-[92vh]">
        <header className="space-y-1 border-b border-nb-border pb-3">
          <h2 className="text-lg font-semibold text-nb-text">
            {t("leads.mapping.title", "Match your columns")}
          </h2>
          <p className="text-xs text-nb-text-3">
            {t(
              "leads.mapping.hint",
              "Tell us what each column of your file contains. Nothing is imported until you confirm.",
            )}
          </p>
        </header>

        {columns.isLoading && (
          <p className="text-sm text-nb-text-3">{t("common.loading")}</p>
        )}
        {columns.isError && (
          <p className="text-sm text-nb-danger">
            {describeApiError(
              columns.error,
              t("leads.mapping.readFailed", "Could not read that file."),
            )}
          </p>
        )}

        {data && (
          <>
            <div className="flex flex-wrap items-end gap-4 rounded-lg border border-nb-border bg-nb-surface/40 px-3 py-2.5 text-xs">
              {data.sheets.length > 1 && (
                <label className="flex flex-col gap-1">
                  <span className="font-medium text-nb-text-3">
                    {t("leads.mapping.sheet", "Sheet")}
                  </span>
                  <select
                    className="input"
                    value={data.sheet}
                    onChange={(e) => {
                      setHeaderRow(null);
                      setSheet(e.target.value);
                    }}
                  >
                    {data.sheets.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <label className="flex flex-col gap-1">
                <span className="font-medium text-nb-text-3">
                  {t("leads.mapping.headerRow", "Header row")}
                </span>
                <input
                  type="number"
                  min={1}
                  className="input w-24"
                  value={data.header_row}
                  onChange={(e) =>
                    setHeaderRow(Math.max(1, Number(e.target.value) || 1))
                  }
                />
              </label>
              {data.reused_from_job && (
                <p className="flex items-center gap-1.5 text-nb-mint">
                  <span aria-hidden>✓</span>
                  {data.reused_from_job === jobId
                    ? // Re-mapping this same file after it errored: these
                      // are the operator's OWN choices, not a past import's.
                      t(
                        "leads.mapping.reusedOwn",
                        "Your previous choices for this file are kept — fix only what was wrong.",
                      )
                    : t(
                        "leads.mapping.reused",
                        "Pre-filled from the last import with these same columns.",
                      )}
                </p>
              )}
            </div>

            {missingRequired.length > 0 && (
              <p className="flex items-start gap-2 rounded-lg border border-nb-warn/40 bg-nb-warn/10 px-3 py-2.5 text-xs text-nb-warn">
                <span aria-hidden className="mt-px">⚠</span>
                <span>
                  {t("leads.mapping.missingRequired", {
                    defaultValue:
                      "Still needed: {{fields}}. Pick the column that holds it.",
                    fields: missingRequired.join(", "),
                  })}
                </span>
              </p>
            )}
            {duplicates.size > 0 && (
              <p className="flex items-start gap-2 rounded-lg border border-nb-danger/40 bg-nb-danger/10 px-3 py-2.5 text-xs text-nb-danger">
                <span aria-hidden className="mt-px">⚠</span>
                <span>
                  {t(
                    "leads.mapping.duplicate",
                    "Two columns point at the same field — each field can come from one column only.",
                  )}
                </span>
              </p>
            )}
            {serverErrors.length > 0 && (
              <ul className="space-y-1 rounded-lg border border-nb-danger/40 bg-nb-danger/10 px-3 py-2.5 text-xs text-nb-danger">
                {serverErrors.map((e, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span aria-hidden className="mt-px">⚠</span>
                    <span>
                      {e.column !== null
                        ? `${t("leads.mapping.columnLabel", "Column")} ${e.column}: `
                        : ""}
                      {e.message}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            <div className="min-h-0 flex-1 space-y-2 overflow-auto pr-1">
              <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)] gap-3 px-1 text-[11px] font-semibold uppercase tracking-wide text-nb-text-3">
                <span>{t("leads.mapping.yourColumn", "Your column")}</span>
                <span>{t("leads.mapping.mapsTo", "Maps to")}</span>
              </div>
              {data.columns.map((col) => {
                const entry = mapping[String(col.index)] ?? {
                  target: "ignore" as const,
                };
                const isDupe = duplicates.has(col.index);
                const mappedQuestion =
                  entry.target === "question" && entry.key
                    ? data.catalog.question.find((e) => e.key === entry.key)
                    : undefined;
                return (
                  <div
                    key={col.index}
                    className={`rounded-lg border p-3 transition-colors ${
                      isDupe
                        ? "border-nb-danger/50 bg-nb-danger/5"
                        : "border-nb-border bg-nb-surface/40"
                    }`}
                  >
                    <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)] gap-4">
                      <div className="min-w-0">
                        <p
                          className="truncate text-sm font-medium text-nb-text"
                          title={col.header}
                        >
                          {col.header}
                        </p>
                        <div className="mt-1.5 flex flex-wrap gap-1">
                          {col.samples.length ? (
                            col.samples.map((s, i) => (
                              <span
                                key={i}
                                className="max-w-[10rem] truncate rounded bg-nb-bg px-1.5 py-0.5 text-[11px] text-nb-text-3"
                                title={s}
                              >
                                {s}
                              </span>
                            ))
                          ) : (
                            <span className="text-[11px] italic text-nb-text-3">
                              {t("leads.mapping.noSamples", "(empty)")}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="min-w-0 space-y-1.5">
                        <div className="flex items-center justify-between gap-2">
                          <TargetBadge target={entry.target} />
                        </div>
                        <select
                          className="input w-full"
                          aria-label={t("leads.mapping.mapsToFor", {
                            defaultValue: 'Maps to, for column "{{header}}"',
                            header: col.header,
                          })}
                          value={encode(entry)}
                          onChange={(e) =>
                            setEntry(
                              col.index,
                              decode(e.target.value, col.header),
                            )
                          }
                        >
                          <option value="ignore">
                            {t("leads.mapping.ignore", "Don't import")}
                          </option>
                          <optgroup
                            label={t(
                              "leads.mapping.groupBeneficiary",
                              "Beneficiary",
                            )}
                          >
                            {data.catalog.beneficiary.map((e) => (
                              <option key={e.key} value={`beneficiary:${e.key}`}>
                                {e.label}
                                {e.required ? " *" : ""}
                              </option>
                            ))}
                          </optgroup>
                          <optgroup
                            label={t("leads.mapping.groupAddress", "Address")}
                          >
                            {data.catalog.address.map((e) => (
                              <option key={e.key} value={`address:${e.key}`}>
                                {e.label}
                              </option>
                            ))}
                          </optgroup>
                          <optgroup
                            label={t(
                              "leads.mapping.groupQuestion",
                              "Campaign question",
                            )}
                          >
                            {data.catalog.question.map((e) => (
                              <option key={e.key} value={`question:${e.key}`}>
                                {e.label}
                                {e.is_conformity
                                  ? ` — ${t("leads.mapping.conformityBadge", "conformity check")}`
                                  : ""}
                              </option>
                            ))}
                            <option value="create:question">
                              {t("leads.mapping.createQuestion", "+ New question…")}
                            </option>
                          </optgroup>
                          <optgroup
                            label={t(
                              "leads.mapping.groupCustomField",
                              "Custom field",
                            )}
                          >
                            {data.catalog.custom_field.map((e) => (
                              <option
                                key={`${e.applies_to}:${e.key}`}
                                value={`custom_field:${e.applies_to}:${e.key}`}
                              >
                                {e.label}
                                {e.country_scope ? ` (${e.country_scope})` : ""}
                              </option>
                            ))}
                            <option value="create:custom_field">
                              {t(
                                "leads.mapping.createCustomField",
                                "+ New custom field…",
                              )}
                            </option>
                          </optgroup>
                        </select>

                        {mappedQuestion?.is_conformity && (
                          <p className="flex items-start gap-1.5 rounded border border-nb-orange/40 bg-nb-orange/10 px-2 py-1.5 text-[11px] text-nb-orange">
                            <span aria-hidden>⚑</span>
                            <span>
                              {t("leads.mapping.conformityNote", {
                                defaultValue:
                                  "Feeds the report's conformity tally — conform when the answer is: {{answer}}",
                                answer:
                                  mappedQuestion.conformity_value === "no"
                                    ? t("questions.commentTriggerNo", "No")
                                    : t("questions.commentTriggerYes", "Yes"),
                              })}
                            </span>
                          </p>
                        )}

                        {entry.create && (
                          <div className="flex flex-wrap gap-2 rounded border border-nb-border bg-nb-bg/40 p-2">
                            <input
                              className="input min-w-[10rem] flex-1"
                              aria-label={t(
                                "leads.mapping.newLabel",
                                "Name for the new field",
                              )}
                              placeholder={t(
                                "leads.mapping.newLabel",
                                "Name for the new field",
                              )}
                              value={entry.create.label}
                              onChange={(ev) =>
                                setEntry(col.index, {
                                  ...entry,
                                  create: {
                                    ...entry.create!,
                                    label: ev.target.value,
                                  },
                                })
                              }
                            />
                            <select
                              className="input w-32"
                              aria-label={t("leads.mapping.newKind", "Type")}
                              value={entry.create.kind}
                              onChange={(ev) =>
                                setEntry(col.index, {
                                  ...entry,
                                  create: {
                                    ...entry.create!,
                                    kind: ev.target.value,
                                  },
                                })
                              }
                            >
                              {(entry.target === "question"
                                ? QUESTION_KINDS
                                : CUSTOM_FIELD_KINDS
                              ).map((k) => (
                                <option key={k} value={k}>
                                  {t(`leads.mapping.kind.${k}`, k)}
                                </option>
                              ))}
                            </select>
                            {entry.target === "custom_field" && (
                              <select
                                className="input w-36"
                                aria-label={t(
                                  "leads.mapping.newAppliesTo",
                                  "Stored on",
                                )}
                                value={entry.create.applies_to ?? "contact"}
                                onChange={(ev) =>
                                  setEntry(col.index, {
                                    ...entry,
                                    create: {
                                      ...entry.create!,
                                      applies_to: ev.target.value,
                                    },
                                  })
                                }
                              >
                                {APPLIES_TO.map((a) => (
                                  <option key={a} value={a}>
                                    {t(`leads.mapping.appliesTo.${a}`, a)}
                                  </option>
                                ))}
                              </select>
                            )}

                            {/* Backlog 2026-09-22 — a question created
                                here could not be marked as a conformity
                                check, nor have its comment box
                                configured, so the operator had to finish
                                the import and then set both again in the
                                questions editor. Same controls as that
                                editor, shown for the same kinds. */}
                            {entry.target === "question" &&
                              COMMENT_KINDS.includes(
                                entry.create.kind as QuestionKind,
                              ) && (
                                <label className="flex w-full items-center gap-2 text-xs text-nb-text-2">
                                  <span className="shrink-0">
                                    {t(
                                      "questions.commentTriggerLabel",
                                      "Show the comment box when the answer is:",
                                    )}
                                  </span>
                                  {/* Defaults to "always", matching the
                                      model and the questions editor. It
                                      used to default to "yes", so a
                                      question created here came out
                                      gated on Yes — hiding the box on
                                      No, the answer that needs
                                      explaining. Same trap the 2026-09-24
                                      model change removed elsewhere. */}
                                  <select
                                    className="input w-28"
                                    value={
                                      entry.create.comment_trigger || "always"
                                    }
                                    onChange={(ev) =>
                                      setEntry(col.index, {
                                        ...entry,
                                        create: {
                                          ...entry.create!,
                                          comment_trigger: ev.target.value as
                                            | "always"
                                            | "yes"
                                            | "no"
                                            | "nsp",
                                        },
                                      })
                                    }
                                  >
                                    <option value="always">
                                      {t(
                                        "questions.commentTriggerAlways",
                                        "Always",
                                      )}
                                    </option>
                                    <option value="yes">
                                      {t("questions.commentTriggerYes", "Yes")}
                                    </option>
                                    <option value="no">
                                      {t("questions.commentTriggerNo", "No")}
                                    </option>
                                    {entry.create.kind ===
                                      "tristate_comment" && (
                                      <option value="nsp">
                                        {t(
                                          "questions.commentTriggerNsp",
                                          "Don't know",
                                        )}
                                      </option>
                                    )}
                                  </select>
                                </label>
                              )}

                            {entry.target === "question" &&
                              CONFORMITY_KINDS.has(entry.create.kind) && (
                                <div className="flex w-full flex-wrap items-center gap-2">
                                  <label className="flex items-center gap-2 text-xs text-nb-text-2">
                                    <input
                                      type="checkbox"
                                      checked={!!entry.create.is_conformity}
                                      onChange={(ev) =>
                                        setEntry(col.index, {
                                          ...entry,
                                          create: {
                                            ...entry.create!,
                                            is_conformity: ev.target.checked,
                                          },
                                        })
                                      }
                                    />
                                    <span>
                                      {t(
                                        "questions.isConformityLabel",
                                        "This is a conformity question",
                                      )}
                                    </span>
                                  </label>
                                  {entry.create.is_conformity && (
                                    <label className="flex items-center gap-2 text-xs text-nb-text-2">
                                      <span className="shrink-0">
                                        {t(
                                          "questions.conformityValueLabel",
                                          "Which answer counts as conform?",
                                        )}
                                      </span>
                                      <select
                                        className="input w-24"
                                        value={
                                          entry.create.conformity_value || "yes"
                                        }
                                        onChange={(ev) =>
                                          setEntry(col.index, {
                                            ...entry,
                                            create: {
                                              ...entry.create!,
                                              conformity_value: ev.target
                                                .value as "yes" | "no",
                                            },
                                          })
                                        }
                                      >
                                        <option value="yes">
                                          {t("questions.commentTriggerYes", "Yes")}
                                        </option>
                                        <option value="no">
                                          {t("questions.commentTriggerNo", "No")}
                                        </option>
                                      </select>
                                    </label>
                                  )}
                                </div>
                              )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <footer className="flex items-center justify-between gap-2 border-t border-nb-border pt-3">
              <p className="text-xs text-nb-text-3">
                {t("leads.mapping.fileLabel", "File")}: {data.source_filename}
              </p>
              <div className="flex gap-2">
                <button type="button" className="btn-ghost" onClick={onCancel}>
                  {t("common.cancel", "Cancel")}
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  disabled={blocked || confirm.isPending}
                  onClick={() => confirm.mutate()}
                >
                  {confirm.isPending
                    ? t("leads.importing")
                    : t("leads.mapping.confirm", "Import with this mapping")}
                </button>
              </div>
            </footer>
          </>
        )}
      </div>
    </div>
  );
}
